import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { TokenService } from '../services/token.service';

/** Endpoints que no llevan token y que nunca deben disparar un refresco. */
const RUTAS_PUBLICAS = [
  'autenticacion/login/',
  'autenticacion/token/refrescar/',
  'autenticacion/token/verificar/',
];

const esPublica = (url: string) => RUTAS_PUBLICAS.some((r) => url.includes(r));

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const router = inject(Router);

  // Las URLs absolutas (incluidas las del propio TokenService) pasan sin tocar.
  if (/^https?:\/\//i.test(req.url)) {
    return next(req);
  }

  // Prefijo del API y slash final (Django lo exige).
  let url = `${environment.apiUrl}/${req.url.replace(/^\/+/, '')}`;
  if (!url.endsWith('/') && !url.includes('?')) url += '/';

  const conAuth = (peticion: HttpRequest<unknown>) => {
    const access = tokenService.accessToken();
    return access
      ? peticion.clone({
          headers: peticion.headers.set('Authorization', `Bearer ${access}`),
        })
      : peticion;
  };

  const peticionBase = req.clone({ url });

  if (esPublica(url)) {
    return next(peticionBase);
  }

  return next(conAuth(peticionBase)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) return throwError(() => error);

      // Si el refresh ya no sirve, no hay nada que intentar.
      return from(tokenService.refreshTokenExpirado()).pipe(
        switchMap((expirado) => {
          if (expirado) {
            tokenService.limpiarToken();
            router.navigate(['/login']);
            return throwError(() => error);
          }

          // El refresco está deduplicado dentro del TokenService: varias
          // peticiones con 401 simultáneo comparten un solo refresco.
          return from(tokenService.refrescarToken()).pipe(
            switchMap((ok) => {
              if (!ok) {
                tokenService.limpiarToken();
                router.navigate(['/login']);
                return throwError(() => error);
              }
              return next(conAuth(peticionBase));
            }),
          );
        }),
      );
    }),
  );
};
